import React, { useMemo } from 'react';
import { Star, ArrowRight } from 'lucide-react';
import { useWorkspace } from '../contexts/WorkspaceContext';

export function TodayFocus() {
  const { tasks, updateTask } = useWorkspace();

  const focusTasks = useMemo(() => {
    return tasks
      .filter(t => t.is_today_focus && t.status !== 'done')
      .slice(0, 3);
  }, [tasks]);

  const handleMoveToProgress = async (taskId) => {
    await updateTask(taskId, { status: 'in_progress' });
  };

  if (focusTasks.length === 0) {
    return (
      <div className="mb-8" data-testid="today-focus-section">
        <div className="flex items-center gap-2 mb-4">
          <Star className="w-4 h-4 text-zinc-500" />
          <h2 className="text-sm font-medium text-zinc-400 uppercase tracking-[0.1em]">
            Dagens Fokus
          </h2>
        </div>
        <div className="p-6 rounded-xl border border-dashed border-white/10 bg-[#0D0D0F] text-center">
          <p className="text-sm text-zinc-500">
            Inga fokusuppgifter valda. Klicka på stjärnan på en uppgift för att lägga till den här.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-8" data-testid="today-focus-section">
      <div className="flex items-center gap-2 mb-4">
        <Star className="w-4 h-4 text-yellow-400" />
        <h2 className="text-sm font-medium text-zinc-400 uppercase tracking-[0.1em]">
          Dagens Fokus
        </h2>
        <span className="text-xs text-zinc-600 ml-auto">{focusTasks.length}/3</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {focusTasks.map((task, index) => (
          <FocusCard 
            key={task.id} 
            task={task} 
            index={index}
            onMoveToProgress={handleMoveToProgress}
          />
        ))}
      </div>
    </div>
  );
}

function FocusCard({ task, index, onMoveToProgress }) {
  const priorityColors = {
    low: 'from-blue-500/20 to-blue-500/5',
    medium: 'from-yellow-500/20 to-yellow-500/5',
    high: 'from-pink-500/20 to-pink-500/5',
  };

  return (
    <div 
      className={`relative p-5 rounded-xl border border-white/10 bg-gradient-to-br ${priorityColors[task.priority]} overflow-hidden group transition-all duration-200 hover:border-white/20`}
      style={{ animationDelay: `${index * 100}ms` }}
      data-testid={`focus-card-${task.id}`}
    >
      <div className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full bg-white/5 text-zinc-500 font-['Outfit'] font-semibold text-sm">
        {index + 1}
      </div>
      
      <h3 className="text-base font-medium text-zinc-100 pr-10 line-clamp-2 mb-2">
        {task.title}
      </h3>
      
      {task.description && (
        <p className="text-xs text-zinc-500 line-clamp-2 mb-3">{task.description}</p>
      )}

      {task.tag && (
        <span className="inline-block px-2 py-0.5 rounded text-xs font-medium badge-blue mb-3">
          {task.tag}
        </span>
      )}

      <button
        onClick={() => onMoveToProgress(task.id)}
        className="flex items-center gap-2 text-xs text-zinc-400 hover:text-zinc-100 transition-colors mt-2 opacity-0 group-hover:opacity-100"
        data-testid={`focus-start-${task.id}`}
      >
        <span>Börja nu</span>
        <ArrowRight className="w-3 h-3" />
      </button>
    </div>
  );
}
